import React, {createContext, useCallback, useContext, useState, useEffect, ReactNode} from 'react';

type LocationState = PermissionState | 'unsupported';
type Coordinates = {lat: number; lng: number};
interface LocationPermissionsContextType {
  state: LocationState;
  hasLocationPermission: boolean;
  requestLocation: (highAccuracy?: boolean) => Promise<Coordinates | null>;
}

const LocationPermissionsContext = createContext<LocationPermissionsContextType | undefined>(undefined);

export const LocationPermissionsProvider: React.FC<{ children: ReactNode }> = ({children}) => {
  const [state, setState] = useState<LocationState>(navigator.geolocation ? 'prompt' : 'unsupported');

  useEffect(() => {
    let disposed = false;
    let permission: PermissionStatus | undefined;
    if (navigator.geolocation && navigator.permissions?.query) {
      navigator.permissions.query({name: 'geolocation'}).then(status => {
        if (disposed) return;
        permission = status;
        setState(status.state);
        status.onchange = () => setState(status.state);
      }).catch(() => {
        // Browsers without permission queries can still ask on a user action.
      });
    }
    return () => {
      disposed = true;
      if (permission) permission.onchange = null;
    };
  }, []);

  const requestLocation = useCallback((highAccuracy = true): Promise<Coordinates | null> => {
    if (!navigator.geolocation) return Promise.resolve(null);
    return new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(position => {
        setState('granted');
        resolve({lat: position.coords.latitude, lng: position.coords.longitude});
      }, error => {
        if (error.code === error.PERMISSION_DENIED) setState('denied');
        resolve(null);
      }, {enableHighAccuracy: highAccuracy, timeout: 5000, maximumAge: highAccuracy ? 0 : 10000});
    });
  }, []);

  return (
    <LocationPermissionsContext.Provider value={{state, hasLocationPermission: state === 'granted', requestLocation}}>
      {children}
    </LocationPermissionsContext.Provider>
  );
};

export const useLocationPermissions = (): LocationPermissionsContextType => {
  const context = useContext(LocationPermissionsContext);
  if (!context) {
    throw new Error('useLocationPermissions must be used within a LocationPermissionsProvider');
  }
  return context;
};
